import { useEffect, useState } from "react";
import api, { API } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Download, ChevronDown, ChevronRight } from "lucide-react";
import RegisterFilters from "@/components/RegisterFilters";
import { useRegisterFilters } from "@/hooks/useRegisterFilters";
import { plural } from "@/lib/utils";

// What a count holds when its request failed — distinct from null, which is still asking.
const FAILED = "failed";

/**
 * The standing questions — the ones with a fixed shape, asked every year.
 *
 * The register answers the ad-hoc ones. This screen answers the recurring ones, and
 * everything it produces means the same thing as the equivalent export from the register,
 * because both are built from the same filter state. Before this, the buttons here opened
 * `/exports/trainings.pdf` with NO query string, so the same button gave the whole
 * register from this screen and the filtered view from the other — with the file itself
 * stating, on its "About this export" sheet, filters that did not match what was on
 * screen when it was asked for.
 */
export default function Reports() {
  const filters = useRegisterFilters();
  const { search } = filters;
  const [trainings, setTrainings] = useState(null);
  const [untrained, setUntrained] = useState(null);
  const [showWho, setShowWho] = useState(false);
  const [attempt, setAttempt] = useState(0);

  // Neither call had a catch, so a failure left its card "counting…" for good. And the
  // count was `.length` of whatever came back — a body that is not a list has a length
  // too, and it would have printed as a number of trainings.
  useEffect(() => {
    let live = true;
    setTrainings(null); setUntrained(null);
    api.get(`/trainings?${search}`)
      .then((r) => { if (live) setTrainings(Array.isArray(r.data) ? r.data.length : FAILED); })
      .catch(() => { if (live) setTrainings(FAILED); });
    api.get(`/dashboard/zero-training?${search}`)
      .then((r) => { if (live) setUntrained(Array.isArray(r.data) ? r.data : FAILED); })
      .catch(() => { if (live) setUntrained(FAILED); });
    return () => { live = false; };
  }, [search, attempt]);

  const url = (path) => `${API}${path}${search ? `?${search}` : ""}`;
  const count = (n, noun, plu) =>
    n === null ? <span className="text-muted-foreground">counting…</span>
      : n === FAILED ? (
        <span className="text-destructive-text">
          could not count —{" "}
          <button type="button" onClick={() => setAttempt((a) => a + 1)}
                  className="text-primary underline underline-offset-2">Try again</button>
        </span>
      ) : plural(n, noun, plu);
  const untrainedCount = Array.isArray(untrained) ? untrained.length : untrained;

  return (
    <div className="space-y-5" data-testid="reports-page">
      <div>
        <div className="overline text-primary">Standard reports</div>
        <h2 className="font-heading font-bold text-3xl mt-1">Reports</h2>
        <p className="text-sm text-muted-foreground mt-1">
          Set the filters once; every download below follows them and says so inside the file.
        </p>
      </div>

      {/* No number until there is one. `trainings ?? 0` put "0 trainings matching" in the
          pill row beside a card that was still counting, or had failed to. */}
      <RegisterFilters state={filters} count={typeof trainings === "number" ? trainings : null} noun="training" />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="p-5 border border-border space-y-3" data-testid="report-trainings">
          <div>
            <div className="font-medium">Training master list</div>
            <p className="text-sm text-muted-foreground">
              Every training the filters describe — {count(trainings, "training")}.
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => window.open(url("/exports/trainings.pdf"))}
                    data-testid="report-trainings-pdf"><Download className="w-4 h-4 mr-1.5" /> PDF</Button>
            <Button variant="outline" onClick={() => window.open(url("/exports/trainings.xlsx"))}
                    data-testid="report-trainings-xlsx"><Download className="w-4 h-4 mr-1.5" /> Excel</Button>
          </div>
        </Card>

        {/* The question an auditor actually asks. The endpoint existed from the start and
            had no screen anywhere — the third thing in this project to be built, enforced
            and unreachable, after the audit log and user management. */}
        <Card className="p-5 border border-border space-y-3" data-testid="report-zero-training">
          <div>
            <div className="font-medium">People with no training in this period</div>
            <p className="text-sm text-muted-foreground">
              Nobody marked Present at a delivered training — {count(untrainedCount, "person", "people")}.
              It is the other side of the dashboard's Coverage figure.
            </p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button variant="outline" onClick={() => window.open(url("/exports/zero-training.xlsx"))}
                    data-testid="report-zero-training-xlsx"><Download className="w-4 h-4 mr-1.5" /> Excel</Button>
            {Array.isArray(untrained) && untrained.length > 0 && (
              <Button variant="ghost" onClick={() => setShowWho((v) => !v)}
                      data-testid="report-zero-training-open">
                {showWho ? <ChevronDown className="w-4 h-4 mr-1.5" /> : <ChevronRight className="w-4 h-4 mr-1.5" />}
                See who
              </Button>
            )}
          </div>
          {/* Listed here rather than behind a link: this IS the screen. A card that can
              only hand you a spreadsheet makes you open Excel to answer a question you
              asked on this page. */}
          {showWho && Array.isArray(untrained) && (
            <div className="border-t border-border pt-3 max-h-72 overflow-y-auto"
                 data-testid="report-zero-training-list">
              {untrained.map((e) => (
                <div key={e.id} className="py-1.5 text-sm flex flex-wrap gap-x-2 items-baseline">
                  <span className="font-medium">{e.full_name}</span>
                  <span className="text-xs text-muted-foreground">
                    {[e.employee_code, e.designation, e.department_name, e.unit_name]
                      .filter(Boolean).join(" · ")}
                  </span>
                </div>
              ))}
            </div>
          )}
        </Card>

        <Card className="p-5 border border-border space-y-3" data-testid="report-employees">
          <div>
            <div className="font-medium">Employee master</div>
            <p className="text-sm text-muted-foreground">
              The register of people, narrowed by unit and department where you set them.
            </p>
          </div>
          <Button variant="outline" onClick={() => window.open(url("/exports/employees.xlsx"))}
                  data-testid="report-employees-xlsx"><Download className="w-4 h-4 mr-1.5" /> Excel</Button>
        </Card>

        <Card className="p-5 border border-border space-y-3" data-testid="report-trainers">
          <div>
            <div className="font-medium">Trainers</div>
            <p className="text-sm text-muted-foreground">
              Sessions delivered, hours, and how many people each trainer has actually trained.
            </p>
          </div>
          <Button variant="outline" onClick={() => window.open(url("/exports/trainers.xlsx"))}
                  data-testid="report-trainers-xlsx"><Download className="w-4 h-4 mr-1.5" /> Excel</Button>
        </Card>

        {/* The raw grain. Every other sheet is an aggregate of this one, which is what
            makes the numbers checkable rather than something to be believed. */}
        <Card className="p-5 border border-border space-y-3" data-testid="report-participants">
          <div>
            <div className="font-medium">Participants — one row per person per training</div>
            <p className="text-sm text-muted-foreground">
              Attendance, who marked it and when, remarks, and the certificate. This is what
              you pivot; every other figure here is a total of it.
            </p>
          </div>
          <Button variant="outline" onClick={() => window.open(url("/exports/participants.xlsx"))}
                  data-testid="report-participants-xlsx"><Download className="w-4 h-4 mr-1.5" /> Excel</Button>
        </Card>

        <Card className="p-5 border border-border space-y-3" data-testid="report-certificates">
          <div>
            <div className="font-medium">Certificates issued register</div>
            <p className="text-sm text-muted-foreground">
              Every certificate number in the period, each marked valid, revoked or superseded.
            </p>
          </div>
          <Button variant="outline" onClick={() => window.open(url("/exports/certificates.xlsx"))}
                  data-testid="report-certificates-xlsx"><Download className="w-4 h-4 mr-1.5" /> Excel</Button>
        </Card>
      </div>

      <Card className="p-5 border border-primary/40 bg-primary/5 flex flex-wrap items-center justify-between gap-3"
            data-testid="report-full">
        <div>
          <div className="font-medium">Everything, in one workbook</div>
          <p className="text-sm text-muted-foreground">
            Trainings · Participants · Employees · Trainers · Certificates · No training —
            one file, all on the filters above, with a sheet saying what they were.
          </p>
        </div>
        <Button onClick={() => window.open(url("/exports/full.xlsx"))} data-testid="report-full-xlsx">
          <Download className="w-4 h-4 mr-1.5" /> Download everything
        </Button>
      </Card>
    </div>
  );
}
